import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Octalbees';
const BASE_URL = 'https://www.octalbees.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/Images/thumbnail_ob.png`;

/**
 * SEOHelmet – Reusable per-page SEO component.
 * Renders unique <title>, <meta description>, <canonical>, OG & Twitter tags.
 *
 * @param {string} title       – Page title (appended with " | Octalbees")
 * @param {string} description – Meta description (max ~155 chars)
 * @param {string} path        – Canonical path e.g. "/about-us"
 * @param {string} [ogImage]   – Optional OG image URL
 * @param {string} [ogType]    – Optional OG type (default: "website")
 * @param {object} [jsonLd]    – Optional JSON-LD structured data object
 * @param {object} [breadcrumbSchema] – Optional BreadcrumbList JSON-LD object
 * @param {object} [extraJsonLd] – Optional additional JSON-LD schema (e.g. FAQPage)
 */
const SEOHelmet = ({ title, description, path = '/', ogImage, ogType = 'website', jsonLd, breadcrumbSchema, extraJsonLd }) => {
    const fullTitle = path === '/' ? title : `${title} | ${SITE_NAME}`;
    const canonicalUrl = `${BASE_URL}${path}`;
    const image = ogImage || DEFAULT_OG_IMAGE;

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonicalUrl} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content={SITE_NAME} />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* JSON-LD Structured Data */}
            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}

            {/* Breadcrumb Structured Data */}
            {breadcrumbSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            )}

            {/* Extra JSON-LD (e.g. FAQPage, HowTo) */}
            {extraJsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(extraJsonLd)}
                </script>
            )}
        </Helmet>
    );
};

export default SEOHelmet;
